import React, {useMemo} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Components
import {PDCLink} from '../../page-designer/assets/pdc-link'

import {
    Box,
    VisuallyHidden,
    Link,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {List, ListItem} from '@chakra-ui/react'

import {categoryUrlBuilder} from '@app/utils/url'
import {NAV_MENU_ITEMS_FILTER} from './constants'

import {PageLink} from './drawer-menu-lvl2-pd-pages'

import useMultiSite from '../../hooks/use-multi-site'

/**
 * SEO Menu
 * As the Drawer Menu is mounted only when open, we need a hidden menu
 * always rendered in the DOM for SEO reasons
 *
 * @param {Object} props - Component props
 * @param {Object} props.root - Root item
 * @param {String} props.itemsKey - Key of the items in the root object
 * @param {Function|String} props.itemsFilter - Filter function or key to filter items
 * @param {Array} props.firstLevelTriggers - First level triggers
 * @param {Array} props.primaryDirectLinks - Primary direct links
 * @param {Array} props.secondaryDirectLinks - Secondary direct links
 * @returns {React.ReactElement}
 */
const SEOMenu = ({
    root,
    itemsKey,
    itemsFilter,
    firstLevelTriggers,
    primaryDirectLinks,
    secondaryDirectLinks,
    trackMyOrderURL
}) => {
    const intl = useIntl()
    const filter = itemsFilter
        ? (item) => (typeof itemsFilter === 'function' ? itemsFilter(item) : !!item[itemsFilter])
        : null

    /**
     * Generates a memoized list of menu items for the SEO menu.
     *
     * This function uses the `useMemo` hook to optimize performance by memoizing the result,
     * ensuring that the computation is only re-executed when `firstLevelTriggers` changes.
     * It returns `null` if there are no first-level triggers.
     *
     * @returns {React.ReactElement[]|null} A collection of `Box` components, each containing
     * a `Heading` and a `List` of `PageLink` components for the pages associated with the
     * first-level triggers. Returns `null` if there are no triggers.
     *
     * @dependencies [firstLevelTriggers] - The list of first-level trigger items that determine
     * the structure of the menu. Changes to this list will cause the memoized function to
     * re-evaluate.
     */
    const filteredItems = useMemo(() => {
        if (!firstLevelTriggers) return null

        return firstLevelTriggers.map((item) => {
            const {id} = item
            const pageIDS = item.regions[0]?.components || []

            return (
                <Box key={id} className="seo-menu-pages-block">
                    <Text
                        className="seo-menu-pages-title"
                        role="heading"
                        aria-level="2"
                    >
                        {item?.data?.title}
                    </Text>

                    <List className="seo-menu-pages-list" paddingLeft="15px">
                        {pageIDS.map((pageID) => (
                            <ListItem key={pageID?.id} className="seo-menu-pages-item">
                                <PageLink
                                    pageId={pageID?.data?.page}
                                    role="menuitem"
                                    tabIndex={-1}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )
        })
    }, [firstLevelTriggers])

    const {buildUrl} = useMultiSite()
    return (
        <>
            {/* Uncomment styles on seo menu wrapper to display it on the web page and test seo menu content */}
            <VisuallyHidden>
                {/* <VisuallyHidden
                style={{
                    height: 'auto',
                    width: '100%',
                    top: 0,
                    left: 0,
                    background: 'white',
                    zIndex: 1000,
                    clip: 'unset',
                    overflow: 'auto',
                    maxHeight: '100vh'
                }}
                > */}
                <Box className="seo-menu-block">
                    <Box role="menu" className="seo-menu" paddingLeft="15px">
                        {/* Categories */}
                        {root?.[itemsKey] &&
                            root?.[itemsKey]
                                .filter((itemLvl1) => !!itemLvl1[NAV_MENU_ITEMS_FILTER])
                                .map((itemLvl1) => {
                                    const {id, name} = itemLvl1
                                    return (
                                        <Box key={id} className="seo-menu-category-level-1-block">
                                            <Text
                                                className="seo-menu-category-level-1-title"
                                                role="heading"
                                                aria-level="2"
                                            >
                                                {name}
                                            </Text>

                                            <Link
                                                as={RouteLink}
                                                tabIndex={-1}
                                                className="seo-menu-link-level-1-all"
                                                to={buildUrl(
                                                    categoryUrlBuilder(
                                                        itemLvl1,
                                                        itemLvl1?.c_categoryType
                                                    )
                                                )}
                                            >
                                                {intl.formatMessage(
                                                    {
                                                        id: 'drawer_menu.link.shop_all',
                                                        defaultMessage: 'View all {name}'
                                                    },
                                                    {
                                                        name: name || ''
                                                    }
                                                )}
                                            </Link>

                                            {itemLvl1?.[itemsKey] && (
                                                <List
                                                    className="seo-menu-category-level-2"
                                                    paddingLeft="15px"
                                                >
                                                    {itemLvl1?.[itemsKey]
                                                        .filter(filter)
                                                        .map((itemLvl2) => {
                                                            const {id, name} = itemLvl2
                                                            return (
                                                                <ListItem
                                                                    key={id}
                                                                    className="seo-menu-category-level-2-item"
                                                                >
                                                                    <Text
                                                                        className="seo-menu-category-level-2-title"
                                                                        role="heading"
                                                                        aria-level="3"
                                                                    >
                                                                        {name}
                                                                    </Text>

                                                                    {itemLvl2?.[itemsKey] && (
                                                                        <List
                                                                            className="seo-menu-category-level-3"
                                                                            paddingLeft="15px"
                                                                        >
                                                                            {itemLvl2?.[itemsKey]
                                                                                .filter(filter)
                                                                                .map((itemLvl3) => {
                                                                                    return (
                                                                                        <ListItem
                                                                                            key={
                                                                                                itemLvl3.id
                                                                                            }
                                                                                            className="seo-menu-category-level-3-item"
                                                                                        >
                                                                                            <Link
                                                                                                as={
                                                                                                    RouteLink
                                                                                                }
                                                                                                tabIndex={
                                                                                                    -1
                                                                                                }
                                                                                                className="seo-menu-link-level-2"
                                                                                                to={buildUrl(
                                                                                                    categoryUrlBuilder(
                                                                                                        itemLvl3
                                                                                                    )
                                                                                                )}
                                                                                            >
                                                                                                {
                                                                                                    itemLvl3.name
                                                                                                }
                                                                                            </Link>
                                                                                        </ListItem>
                                                                                    )
                                                                                })}
                                                                        </List>
                                                                    )}

                                                                    <Link
                                                                        as={RouteLink}
                                                                        tabIndex={-1}
                                                                        className="seo-menu-link-level-2-all"
                                                                        to={buildUrl(
                                                                            categoryUrlBuilder(
                                                                                itemLvl2
                                                                            )
                                                                        )}
                                                                    >
                                                                        {intl.formatMessage(
                                                                            {
                                                                                id: 'drawer_menu.link.shop_all',
                                                                                defaultMessage:
                                                                                    'View all {name}'
                                                                            },
                                                                            {
                                                                                name: name || ''
                                                                            }
                                                                        )}
                                                                    </Link>
                                                                </ListItem>
                                                            )
                                                        })}
                                                </List>
                                            )}
                                        </Box>
                                    )
                                })}

                        {/* First level triggers Navigation */}
                        {filteredItems}

                        {/* Primary direct links */}
                        {primaryDirectLinks && (
                            <List className="seo-menu-direct-links-list" paddingTop="15px">
                                {primaryDirectLinks.map((item, i) => {
                                    return (
                                        <ListItem key={i} className="seo-menu-direct-link-item">
                                            <PDCLink
                                                tabIndex={-1}
                                                className="seo-menu-direct-link"
                                                custom={item?.custom}
                                                label={item?.data?.label}
                                                role="menuitem"
                                            ></PDCLink>
                                        </ListItem>
                                    )
                                })}
                            </List>
                        )}

                        {/* Secondary direct links */}
                        {secondaryDirectLinks && (
                            <List
                                className="seo-menu-secondary-direct-links-list"
                                paddingTop="15px"
                            >
                                {secondaryDirectLinks.map((item, i) => (
                                    <ListItem key={i} className="seo-menu-direct-link-item">
                                        <PDCLink
                                            tabIndex={-1}
                                            className="seo-menu-direct-link"
                                            custom={item?.custom}
                                            label={item?.data?.label}
                                            role="menuitem"
                                        />
                                    </ListItem>
                                ))}
                                {/* Track my order */}
                                {trackMyOrderURL && (
                                    <ListItem
                                        key="seo-track-my-order"
                                        className="seo-menu-direct-link-item"
                                    >
                                        <PDCLink
                                            tabIndex={-1}
                                            className="seo-menu-direct-link"
                                            url={buildUrl(trackMyOrderURL)}
                                            role="menuitem"
                                        >
                                            {intl.formatMessage({
                                                id: 'help_link.trackmyorder',
                                                defaultMessage: 'Track my order'
                                            })}
                                        </PDCLink>
                                    </ListItem>
                                )}
                            </List>
                        )}

                        {/* Footer menu links */}
                        <List className="seo-menu-bottom-list" paddingTop="15px">
                            <ListItem key="seo-menu-account-link">
                                <Link tabIndex={-1} to={buildUrl('/account')}>
                                    {intl.formatMessage({
                                        id: 'drawer_menu.button.account_details',
                                        defaultMessage: 'My account'
                                    })}
                                </Link>
                            </ListItem>
                            <ListItem key="seo-menu-home-link">
                                <Link tabIndex={-1} to={'/'} />
                            </ListItem>
                        </List>
                    </Box>
                </Box>
            </VisuallyHidden>
        </>
    )
}

SEOMenu.propTypes = {
    root: PropTypes.object,
    itemsKey: PropTypes.string,
    itemsFilter: PropTypes.string,
    firstLevelTriggers: PropTypes.array,
    primaryDirectLinks: PropTypes.array,
    secondaryDirectLinks: PropTypes.array,
    trackMyOrderURL: PropTypes.string
}

export default SEOMenu
